import * as React from 'react'
import styled from 'styled-components'
import { PlanOption } from './PlanOption'
import { colors, CTAPrimaryBlue } from '@components/shared'
import CheckIcon from '@components/shared/icons/CheckIcon'
import { config } from 'react-awesome-styled-grid'
import type { SubscriptionLengthType } from '../../../../../flowTypes'

export type PropsType = {
  header: string,
  subHeader?: string,
  textPaywallValueProps: string[],
  textAnnualArialLabel: string,
  textMonthlyCtaWebApp: string,
  textMonthlyArialLabel: string,
  plan?: SubscriptionLengthType,
  price?: string,
  showHover: boolean,
  promoValid?: boolean,
  isPromoFlowPrice?: boolean
}

export type StateType = {
  selection: 'monthly' | 'annual'
}

export type DispatchPropsType = {
  choosePlan: (plan: 'monthly' | 'annual') => void
}

class ChoosePlan extends React.PureComponent<PropsType & DispatchPropsType, StateType> {
  constructor (props: PropsType & DispatchPropsType) {
    super(props)
    this.state = {
      selection:
        this.props.plan === 'annual' || this.props.plan === 'monthly'
          ? this.props.plan
          : 'annual'
    }
    this.chooseAnnual = this.chooseAnnual.bind(this)
    this.chooseMonthly = this.chooseMonthly.bind(this)
    this.selectPlan = this.selectPlan.bind(this)
  }

  chooseAnnual (e: any): void {
    e.preventDefault()

    if (this.state.selection === 'annual') {
      this.selectPlan(e)
    } else {
      this.setState({
        selection: 'annual'
      })
    }
  }

  chooseMonthly (e: any): void {
    e.preventDefault()
    if (this.state.selection === 'monthly') {
      this.selectPlan(e)
    } else {
      this.setState({
        selection: 'monthly'
      })
    }
  }

  selectPlan (e: any): void {
    e.preventDefault()
    if (this.state.selection === 'annual') {
      this.props.choosePlan('annual')
    } else {
      this.props.choosePlan('monthly')
    }
  }

  render (): React.ReactNode {
    const {
      header,
      subHeader,
      textPaywallValueProps,
      textAnnualArialLabel,
      textMonthlyArialLabel,
      plan,
      showHover,
      isPromoFlowPrice
    } = this.props

    let annualPriceText = `$69.99 USD Annually ($5.82/month)`
    if (isPromoFlowPrice) { // this can be made dynamic in the future
      annualPriceText = `$47.99 USD Annually ($3.99/month)`
    }
    // `${textCurrencySymbol}${textAnnualYearlyPrice} ${textCurrencyISO} ${textWebAppAnnualCycle} (${textCurrencySymbol}${textAnnualPrice} /month)`
    const monthlyPriceText = `$12.99 USD Monthly`
    // `${textCurrencySymbol}${textMonthlyPrice} ${textCurrencyISO} ${textWebAppMonthlyCycle}`
    return (
      <Container id="choose-plan-container">
        <Header>{header}</Header>

        {subHeader && <SubHeader>{subHeader}</SubHeader>}

        {!!textPaywallValueProps && (
          ValueProps(textPaywallValueProps)
        )}

        <PlanOptionsContainer>
          <PlanOption
            name={'annual-button'}
            onClick={this.chooseAnnual}
            faded={plan !== 'annual'}
            selected={this.state.selection === 'annual'}
            showHover={showHover || false}
            text={annualPriceText}
            arialLabel={textAnnualArialLabel}
            bannerText={'Most Popular'}
          />
          <PlanOption
            name={'monthly-button'}
            onClick={this.chooseMonthly}
            faded={plan !== 'monthly'}
            selected={this.state.selection === 'monthly'}
            showHover={showHover || false}
            text={monthlyPriceText}
            arialLabel={textMonthlyArialLabel}
          />
        </PlanOptionsContainer>

        <PillCtaContainer>
          <CTAPrimaryBlue
            className="subscribe-cta"
            type="button"
            onClick={this.selectPlan}
            color={colors.midnightBlue}
            backgroundColor={colors.white}
            fontWeight={'bold'}
            disabledColor={colors.white}
            disabledBackgroundColor={colors.midnightBlue}
            disabledBorderColor={colors.white}
            borderRadius={'27.5px'}
            borderColor={colors.transparent}
            height={'55px'}
            hoverColor={'rgba(255,255,255, 0.5)'}
          >
            {this.props.promoValid
              ? (
              `SUBSCRIBE NOW`
                )
              : (
              `${this.state.selection === 'annual' ? 'START YOUR 7-DAY FREE TRIAL' : 'PURCHASE MONTHLY'}`
                )}
          </CTAPrimaryBlue>
        </PillCtaContainer>
      </Container>
    )
  }
}

export default ChoosePlan

export const ValueProps = (values: string[]): React.ReactNode => {
  return values.map((value: string): any => {
    return (
      <ValueProp key={value}>
        <CheckIcon />
        <ValuePropText>{value}</ValuePropText>
      </ValueProp>
    )
  })
}
const PillCtaContainer = styled.div<any>`
  align-self: center;
  display: flex;
  margin: 7px 0 0;
  width: 100%;
`
const ValueProp = styled.div<any>`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin: 0 0 16px;
`
const ValuePropText = styled.p<any>`
  color: ${colors.white};
  font-family: 'Good Sans Light';
  font-style: normal;
  font-weight: 300;
  line-height: 150%;
  margin: 0 0 0 12px;
  letter-spacing: 0.1px;
  font-size: 12px;
  ${(props: any): any => config(props).media.md`
    font-size: 16px;
  `};
`
const Container = styled.div<any>`
  max-width: 414px;
  align-content: center;
  display: flex;
  flex-direction: column;
  margin: 72px 20px 20px;
`
const Header = styled.h1<any>`
  color: ${colors.white};
  font-family: 'Queens Condensed Light';
  font-style: normal;
  font-weight: 500;
  margin: 0 0 20px;
  text-align: center;
  max-width: 560px;
  align-self:center;
  font-size: 45px;
  line-height: 110%;
  letter-spacing: 0.4px;
  ${(props: any): any => config(props).media.md`
    white-space: pre;
  `};
`
const SubHeader = styled.h2<any>`
  color: ${colors.white};
  font-family: "Good Sans Light";
  font-style: normal;
  font-weight: normal;
  letter-spacing: 0.2px;
  margin: 0 0 20px;
  padding: 0;
  text-align: center;
  font-size: 12px;
  line-height: 150%;
  ${(props: any): any => config(props).media.md`
    white-space: pre;
    font-size: 16px;
    line-height: 150%;
  `};
`
const PlanOptionsContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  margin: 13px 0 0;
`
